import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent } from '../../../../components/ui/Card.js';
import { Button } from '../../../../components/ui/Button.js';
import { StatusBadge } from '../../../../components/ui/StatusBadge.js';
import { Money } from '../../../../components/ui/Money';
import { useToast } from '../../../../components/ui/Toast.js';
import { Plus, Landmark, FileText } from 'lucide-react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../lib/locale';

import { useChitActFees, useApproveChitActFee } from '../../../../features/finance/hooks';
import { usePageTitle } from '../../../../lib/hooks';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { defineListPage } from '@repo/list';
import { DataTable, SearchInput } from '@repo/web-list';

export const Route = createFileRoute('/_auth/finance/chit-act-fees/')({
  component: ChitActFeesPage,
});

interface ActFeeRecord {
  id: string;
  schemeId: string | null;
  groupCode: string | null;
  feeType: 'BYLAW_REGISTRATION' | 'MINUTES_FILING' | 'DECLARATION_COMMENCEMENT' | 'AUDITED_BALANCE_SHEET_FILING';
  amountPaise: number;
  challanNumber: string | null;
  dueDate: string;
  status: 'PENDING' | 'APPROVED';
  notes: string | null;
}

const FEE_TYPE_LABELS: Record<ActFeeRecord['feeType'], string> = {
  BYLAW_REGISTRATION: 'Bylaw Registration Fee (Sec 4)',
  MINUTES_FILING: 'Minutes of Meeting Filing Fee (Sec 38)',
  DECLARATION_COMMENCEMENT: 'Declaration of Commencement (Sec 9)',
  AUDITED_BALANCE_SHEET_FILING: 'Audited Balance Sheet Filing (Sec 24)',
};

function ChitActFeesPage() {
  usePageTitle('Chit Act Filing Fees');
  const { toast } = useToast();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const { data: response, isLoading, isError, error, refetch } = useChitActFees(query ? { query } : undefined);
  const records = (response?.data ?? []) as ActFeeRecord[];

  const approveMutation = useApproveChitActFee();

  const handleApproveChallan = (id: string) => {
    approveMutation.mutate(id, {
      onSuccess: () => {
        toast({
          type: 'success',
          title: 'Challan Cleared',
          description: 'Chit Act filing receipt has been verified with Treasury clearing.',
        });
      },
      onError: (err: unknown) => {
        toast({
          type: 'error',
          title: 'Failed to Clear Challan',
          description: (err as { message?: string })?.message || 'Could not verify the challan clearance. Please try again.',
        });
      },
    });
  };

  const filteredRecords = records.filter(r =>
    (r.groupCode ?? '').toLowerCase().includes(query.toLowerCase()) ||
    (r.challanNumber ?? '').toLowerCase().includes(query.toLowerCase()) ||
    FEE_TYPE_LABELS[r.feeType].toLowerCase().includes(query.toLowerCase())
  );

  const totalPaisePaid = records.filter(r => r.status === 'APPROVED').reduce((acc, r) => acc + r.amountPaise, 0);

  const config = defineListPage<ActFeeRecord>({
    variant: 'data-table',
    resource: 'chit-act-fees',
    columns: [
      { id: 'challanNumber', header: 'Challan Number', accessor: 'challanNumber', renderType: 'code' },
      { id: 'groupCode', header: 'Chit Scheme', accessor: 'groupCode', renderType: 'text' },
      { id: 'feeType', header: 'Statutory Fee Type', accessor: 'feeType', renderType: 'text' },
      { id: 'amountPaise', header: 'Amount Paid', accessor: 'amountPaise', renderType: 'money', numeric: true },
      { id: 'dueDate', header: 'Challan Date', accessor: 'dueDate', renderType: 'date' },
      { id: 'status', header: 'Treasury Status', accessor: 'status', renderType: 'text' },
      { id: 'notes', header: 'Notes', accessor: 'notes', renderType: 'text' },
      { id: 'actions', header: 'Actions', accessor: 'id', renderType: 'text' },
    ],
  });

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Chit Act Filing Fees"
        subtitle="Log, track, and audit statutory fees paid via Government Challans to the Registrar of Chits under statutory sections."
        action={
          <Button variant="primary" onClick={() => navigate({ to: '/finance/chit-act-fees/new' })}>
            <Plus className="w-4 h-4 mr-2" />
            Log Filing Challan
          </Button>
        }
      />
      <div className="flex-1 w-full min-h-0">
        <Card className="shadow-sm max-w-md mb-6">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Total Cleared Statutory Filings</span>
              <h2 className="text-2xl font-bold text-success-text">
                <Money paise={totalPaisePaid} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
              </h2>
            </div>
            <div className="p-3 bg-success-bg text-success-500 rounded-lg">
              <Landmark className="w-6 h-6" />
            </div>
          </CardContent>
        </Card>

        <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
          <DataTable
            onRetry={() => refetch()}
            config={config}
            data={filteredRecords}
            total={filteredRecords.length}
            page={1}
            limit={filteredRecords.length || 1}
            onPageChange={() => {}}
            loading={isLoading}
            error={isError ? (error?.message || 'Failed to load filing fees') : undefined}
            toolbarActions={
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search group code, challan, fee type..."
                className="w-72"
              />
            }
            customRenderers={{
              feeType: (row: ActFeeRecord) => (
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-accent-500 shrink-0" />
                  <span className="font-semibold">{FEE_TYPE_LABELS[row.feeType]}</span>
                </div>
              ),
              status: (row: ActFeeRecord) => (
                <StatusBadge
                  status={row.status === 'APPROVED' ? 'success' : 'warning'}
                  label={row.status}
                />
              ),
              actions: (row: ActFeeRecord) => (
                row.status === 'PENDING' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-success-500 hover:text-success-text font-semibold"
                    onClick={() => handleApproveChallan(row.id)}
                    loading={approveMutation.isPending}
                  >
                    Verify Clearance
                  </Button>
                ) : null
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}
